import { Average } from '@/engine/utils/Average';
import type { ICameraModifier, IPointerAction } from '../PointerHandlerAdjustCamera';
import type { PointerSyntheticEvent } from '../PointerSyntheticEvent';
import type * as THREE from 'three';
import type { IRaycaster } from '@/engine/app/interfaces';

export enum _Mode {
  NONE = 0,
  ROTATE = 1,
  TOUCH_ZOOM_PAN_ROTATE = 2
}

class _Pointer {
  private readonly _pointers = new Map<number, THREE.Vector2>();

  private _commonCenter?: THREE.Vector2;

  private _commonDirection?: THREE.Vector2;

  public clone(): _Pointer {
    const ret = new _Pointer();
    for (const [key, val] of this._pointers) {
      ret._pointers.set(key, val.clone());
    }
    ret._commonCenter = this._commonCenter?.clone();
    return ret;
  }

  private _updateCache(): void {
    this._commonCenter = undefined;
    this._commonDirection = undefined;
    const values = Array.from(this._pointers.values());
    if (values.length == 1) {
      this._commonCenter = values[0].clone();
      this._commonDirection = undefined;
    } else if (values.length > 1) {
      this._commonDirection = values[1].clone().sub(values[0]);
      this._commonCenter = values[1].clone().add(values[0]).divideScalar(2);
    }
  }

  public add(id: number, pos: THREE.Vector2): void {
    this._pointers.set(id, pos);
    this._updateCache();
  }

  public remove(id: number): boolean {
    if (this._pointers.delete(id)) {
      this._updateCache();
      return true;
    }
    return false;
  }

  public update(id: number, pos: THREE.Vector2): void {
    this.add(id, pos);
  }

  public size(): number {
    return this._pointers.size;
  }

  public getCommonCenter(): THREE.Vector2 | undefined {
    return this._commonCenter;
  }

  public getCommonDirection(): THREE.Vector2 | undefined {
    return this._commonDirection;
  }
}

export class TouchPointerAction implements IPointerAction {
  private readonly _rayCaster: IRaycaster;

  private readonly _cameraModifier: ICameraModifier;

  private readonly _noRotate: boolean;

  private _activeMode = _Mode.NONE;

  private _prevPointer2d = new _Pointer();

  private _rayCastedPoint3d?: THREE.Vector3;

  private readonly _averageTouchWidth = new Average(10);

  private readonly _touchWidthRatio = 2.5;

  private readonly _rotateAroundCenterZoom;

  private _prevCenter?: THREE.Vector2;

  private _prevDirection?: THREE.Vector2;

  private _timeout?: number;

  public constructor(
    rayCaster: IRaycaster,
    cameraModifier: ICameraModifier,
    noRotate: boolean,
    rotateAroundCenterZoom: number
  ) {
    this._rayCaster = rayCaster;
    this._cameraModifier = cameraModifier;
    this._noRotate = noRotate;
    this._rotateAroundCenterZoom = rotateAroundCenterZoom;
  }

  public addPointer(event: PointerSyntheticEvent): void {
    const { pointerId, width } = event.originalEvent;

    this._prevPointer2d.add(pointerId, this._cameraModifier.getPointerEventCoordinates(event.originalEvent));
    this._averageTouchWidth.update(width);
    if (this._prevPointer2d.size() == 2) {
      this._activeMode = _Mode.TOUCH_ZOOM_PAN_ROTATE;
      this._rayCastedPoint3d = undefined;
    } else if (!this._noRotate) {
      const pointer2d = this._prevPointer2d.getCommonCenter();
      if (pointer2d && this._cameraModifier.getCameraZoom() > this._rotateAroundCenterZoom) {
        this._rayCastedPoint3d = this._rayCaster.rayCast(pointer2d)?.point;
      } else {
        this._rayCastedPoint3d = undefined;
      }
      this._activeMode = _Mode.ROTATE;
    }
    this._prevCenter = undefined;
    this._prevDirection = undefined;
  }

  public removePointer(event: PointerSyntheticEvent): boolean {
    this._prevPointer2d.remove(event.originalEvent.pointerId);
    if (this._prevPointer2d.size() == 2) {
      this._activeMode = _Mode.TOUCH_ZOOM_PAN_ROTATE;
      this._rayCastedPoint3d = undefined;
    } else if (this._prevPointer2d.size() <= 1) {
      this._activeMode = _Mode.NONE; // to avoid camera jumps, lets discard any mode if finger removed
      this._rayCastedPoint3d = undefined;
    }
    this._prevCenter = undefined;
    this._prevDirection = undefined;
    return this._prevPointer2d.size() == 0;
  }

  public updatePointer(event: PointerSyntheticEvent): boolean {
    if (this._activeMode == _Mode.NONE) {
      return false;
    }

    if (this._timeout === undefined) {
      this._timeout = setTimeout(() => {
        this._updatePointer();
        this._timeout = undefined;
      }, 5) as unknown as number;
    }

    const { pointerId, width } = event.originalEvent;
    this._prevPointer2d.update(pointerId, this._cameraModifier.getPointerEventCoordinates(event.originalEvent));
    this._averageTouchWidth.update(width);

    return false;
  }

  private _updatePointer(): void {
    const currCenter = this._prevPointer2d.getCommonCenter();
    const currDirection = this._prevPointer2d.getCommonDirection();
    const touchWidth = this._averageTouchWidth.get();
    if (!currCenter || touchWidth === undefined) {
      return;
    }
    if (this._prevCenter) {
      switch (this._activeMode) {
        case _Mode.ROTATE:
          this._cameraModifier.rotateCamera(
            this._rayCastedPoint3d ?? this._cameraModifier.getCenterPoint(),
            this._prevCenter,
            currCenter
          );
          break;
        case _Mode.TOUCH_ZOOM_PAN_ROTATE: {
          this._cameraModifier.panCamera(this._prevCenter, currCenter);
          const isZoomRotateThresholdMet = currDirection && currDirection.length() > touchWidth * this._touchWidthRatio;
          if (this._prevDirection && isZoomRotateThresholdMet) {
            const zoomAmount = (currDirection.length() - this._prevDirection.length()) / 2;
            this._cameraModifier.zoomCameraDirect(currCenter, zoomAmount);
            if (!this._noRotate) {
              this._cameraModifier.rotateCameraInPlaneDirect(currCenter, this._prevDirection, currDirection);
            }
          }
          break;
        }
      }
    }
    this._prevCenter = currCenter;
    this._prevDirection = currDirection;
    this._cameraModifier.notifyCameraChanged();
  }
}
